import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  Tooltip,
} from '@material-ui/core'
import DragIcon from '@material-ui/icons/DragIndicatorOutlined'
import { CoreButton } from 'src/components/core'
import { Icon } from '@iconify/react'
import TextFieldPopup from 'src/components/textfield-popup'
import SuggestionSkeleton from 'src/components/suggestion-skeleton'
import { withStyles } from '@material-ui/core/styles'
import { isMobile } from 'react-device-detect'
import styles from '../styles'
import { useSelector } from 'react-redux'

const ModuleHeader = ({
  classes,
  module,
  sectionIndex,
  moduleIndex,
  moduleTitleRefs,
  expandedModules,
  onModuleTitleChange,
  onPageTitleSuggestions,
  pageTitleSuggestions,
  onToggleModule,
  onDeletePage,
  dragHandleProps,
  documentFollowing,
  setDocumentFollowing,
}) => {
  const moduleKey = `${sectionIndex}-${moduleIndex}`
  const isExpanded = expandedModules[moduleKey]
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const hasRequestedSuggestions = useRef(false)
  const [isHovering, setIsHovering] = useState(false)
  const attachments = useSelector(state => state.aICreator.attachments)
  const handleTitleFocus = useCallback(() => {
    // Show suggestions when focusing on empty module title or default placeholder
    if (
      !module.module_title ||
      module.module_title.trim() === '' ||
      module.module_title === 'New Module'
    ) {
      // Prevent multiple API calls
      if (hasRequestedSuggestions.current) {
        return
      }

      setShowSuggestions(true)
      setIsLoading(true)
      hasRequestedSuggestions.current = true

      onPageTitleSuggestions(sectionIndex, moduleIndex)
    }
  }, [module.module_title, sectionIndex, moduleIndex, onPageTitleSuggestions])

  const handleSuggestionClick = suggestion => {
    onModuleTitleChange(suggestion, sectionIndex, moduleIndex)
    setShowSuggestions(false)
    hasRequestedSuggestions.current = false
  }

  const handleRegenerate = () => {
    setIsLoading(true)
    onPageTitleSuggestions(sectionIndex, moduleIndex)
  }

  const handleDeletePage = () => {
    onDeletePage(sectionIndex, moduleIndex)
  }

  // Update loading state when suggestions are received
  useEffect(() => {
    if (pageTitleSuggestions.length > 0) {
      setIsLoading(false)
    }
  }, [pageTitleSuggestions])

  // Reset the flag when popup is closed
  const handlePopupClose = useCallback(() => {
    setShowSuggestions(false)
    hasRequestedSuggestions.current = false
  }, [])

  const goalsWithAttachmentCheck =
    (attachments.length === 0 && documentFollowing === 'reference') ||
    (attachments.length > 0 && documentFollowing !== 'reference')

  // Check if this is a quiz, feedback, or survey module
  const isQuizOrFeedback =
    module.content_type === 'quiz' ||
    module.content_type === 'feedback' ||
    module.content_type === 'survey'

  return (
    <Box
      className={classes.pageHeader}
      onMouseEnter={() => {
        setIsHovering(true)
      }}
      onMouseLeave={() => {
        setIsHovering(false)
      }}
    >
      <span {...dragHandleProps} className={classes.dragHandle}>
        <Icon
          icon="icon-park:drag"
          color="#838D98"
          style={{
            fontSize: isMobile ? '16px' : '20px',
            color: '#838D98',
          }}
          className={classes.dragIcon}
        />
      </span>

      <div
        ref={el => {
          if (!moduleTitleRefs.current) moduleTitleRefs.current = []
          const key = `${sectionIndex}-${moduleIndex}`
          moduleTitleRefs.current[key] = el
        }}
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Page Title"
        className={classes.pageTitleText}
        onBlur={() => {
          const key = `${sectionIndex}-${moduleIndex}`
          const title = moduleTitleRefs.current[key]?.textContent
          if (module.module_title !== title) {
            onModuleTitleChange(title, sectionIndex, moduleIndex)
          }
        }}
        onFocus={handleTitleFocus}
        style={{ cursor: 'text' }}
      >
        {module.module_title}
      </div>

      {/* Page Title Suggestions Popup */}
      <TextFieldPopup
        anchorEl={moduleTitleRefs.current?.[`${sectionIndex}-${moduleIndex}`]}
        open={showSuggestions}
        onClose={handlePopupClose}
        placement="bottom-start"
        offset={4}
        maxHeight={250}
      >
        <div className={classes.popupContent}>
          <div className={classes.popupTitleContainer}>
            <Typography
              fontSize={14}
              fontWeight={400}
              className={classes.popupTitle}
            >
              Suggested page titles:
            </Typography>
            {pageTitleSuggestions.length > 0 && (
              <CoreButton
                size="small"
                variant="text"
                color="secondary"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon icon="hugeicons:exchange-01" fontSize={16} />
                  )
                }
                onClick={handleRegenerate}
                disabled={isLoading}
              >
                {isLoading ? 'Generating...' : 'Regenerate'}
              </CoreButton>
            )}
          </div>

          <List dense className={classes.suggestionList}>
            {isLoading ? (
              <SuggestionSkeleton />
            ) : pageTitleSuggestions.length > 0 ? (
              pageTitleSuggestions.map((suggestion, suggestionIndex) => (
                <ListItem
                  key={suggestionIndex}
                  button
                  className={classes.suggestionItem}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className={classes.suggestionItemIcon}>
                    <img
                      src="/assets/AI-Assistant-icon.svg"
                      width={isMobile ? 16 : 24}
                      height={isMobile ? 16 : 24}
                      alt="AI Assistant"
                    />
                  </div>
                  <ListItemText
                    primary={suggestion}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      className: classes.suggestionText,
                    }}
                  />
                </ListItem>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography variant="body2" style={{ color: '#637381' }}>
                  {pageTitleSuggestions.length === 0
                    ? 'No suggestions available. Try regenerating.'
                    : 'Enter a course topic to get suggestions.'}
                </Typography>
              </div>
            )}
          </List>
        </div>
      </TextFieldPopup>
      <Icon
        icon="hugeicons:delete-01"
        style={{
          cursor: 'pointer',
          fontSize: '16px',
          visibility: isHovering ? 'visible' : 'hidden',
          color: '#002E47',
          marginRight: '8px',
        }}
        onClick={handleDeletePage}
      />

      {/* Only show expand/collapse for modules that have goals (not quiz/feedback/survey) */}
      {!goalsWithAttachmentCheck && !isQuizOrFeedback && (
        <Tooltip title="Show page details">
          <Icon
            onClick={() => onToggleModule(moduleKey)}
            icon="line-md:chevron-down"
            style={{
              cursor: 'pointer',
              fontSize: '16px',
              color: '#000',
              transition: 'transform 0.3s ease',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Tooltip>
      )}
    </Box>
  )
}

export default withStyles(styles)(ModuleHeader)
